import Immutable from 'seamless-immutable';
import { createSelector } from 'reselect';
import { selectViewConfig } from './Cards';
import { getBoardConfig, handleDeletedAuthoritiesInFieldConfigs, verifyName } from '../utils/FieldUtil';
import { Field } from '@/models/card/CardModel';
import { Board } from '@/models/BoardModel';
import { User } from '@/models/permission/AuthorityModel';
import { handleDeletedAuthorities } from '@/utils/AuthorityUtil.ts';
import { MilestoneConfig } from '@/models/MilestoneModel.ts';

// Types
export const types = {
  //
  // FETCH BOARDS
  //
  FETCH_BOARDS_REQUEST: 'FETCH_BOARDS_REQUEST',
  FETCH_BOARDS_SUCCESS: 'FETCH_BOARDS_SUCCESS',
  FETCH_BOARDS_ERROR: 'FETCH_BOARDS_ERROR',

  //
  // FETCH CURRENT BOARD
  //
  FETCH_ACTIVE_BOARD_REQUEST: 'FETCH_ACTIVE_BOARD_REQUEST',
  FETCH_ACTIVE_BOARD_SUCCESS: 'FETCH_ACTIVE_BOARD_SUCCESS',
  FETCH_ACTIVE_BOARD_ERROR: 'FETCH_ACTIVE_BOARD_ERROR',

  //
  // EXPAND / COLLAPSE WEEK COLUMNS
  //
  EXPAND_COLLAPSE_WEEK_COLUMN: 'EXPAND_COLLAPSE_WEEK_COLUMN',

  //
  // ROOM MEMBERS
  //
  FETCH_ROOM_MEMBERS_REQUEST: 'FETCH_ROOM_MEMBERS_REQUEST',
  FETCH_ROOM_MEMBERS_SUCCESS: 'FETCH_ROOM_MEMBERS_SUCCESS',
  FETCH_ROOM_MEMBERS_ERROR: 'FETCH_ROOM_MEMBERS_ERROR',

  //
  // SCROLL POS
  //
  SET_TILE_SCROLL_POS: 'SET_TILE_SCROLL_POS',
  SET_WEEK_SCROLL_POS: 'SET_WEEK_SCROLL_POS',
};

// If the action's payload holds a field configuration, then for each member field, verify the names of users that can
// be selected. Invited members have not yet set a name; for those users, use the e-mail address instead. For other
// names, just remove leading and trailing whitespace. Perform the same check for the users in the "from" and "to"
// fields.
// todo: move to utils?
const setNameForInvitedUsers = (action: any) => {
  const fieldConfig = action.payload.activeBoard.field_config;
  const from = fieldConfig.allowed_from_authorities;
  const to = fieldConfig.allowed_to_authorities;
  const fields = fieldConfig.fields;

  if (from) {
    from.forEach(verifyName);
  }
  if (to) {
    to.forEach(verifyName);
  }
  if (fields) {
    fields.forEach((field: Field) => {
      if (field.type === 'member' && field.limited_to) {
        field.limited_to.forEach(verifyName);
      }
    });
  }
};

const initialState = Immutable({
  isLoadingBoards: false,
  isRequested: {},
  isLoadingRoomMembers: false,
  boards: [],
  isLoadingActiveBoard: false,
  isRequestedActiveBoard: {},
  activeBoard: null,
  viewConfig: {
    expandedColumns: [],
    tileScrollPos: {
      scrollX: null,
      scrollY: null,
    },
    weekScrollPos: {
      scrollX: null,
      scrollY: null,
    },
  },
  roomMembers: [],
  roomMembersRequested: {},
});

// Reducer
export default (state = initialState, action: any) => {
  switch (action.type) {
    //
    // FETCH BOARDS
    //
    case types.FETCH_BOARDS_REQUEST:
      return state.merge({
        isLoadingBoards: true,
        isRequested: { ...action.payload.params },
      });
    case types.FETCH_BOARDS_SUCCESS:
      return state.merge({
        isLoadingBoards: false,
        boards: action.payload.boards,
      });
    case types.FETCH_BOARDS_ERROR:
      return state.merge({
        isLoadingBoards: false,
      });

    //
    // FETCH CURRENT BOARD
    //
    case types.FETCH_ACTIVE_BOARD_REQUEST:
      return state.merge({
        isLoadingActiveBoard: true,
        isRequestedActiveBoard: { ...action.payload.params },
      });
    case types.FETCH_ACTIVE_BOARD_SUCCESS:
      setNameForInvitedUsers(action);
      return state.merge({
        isLoadingActiveBoard: false,
        activeBoard: action.payload.activeBoard,
      });
    case types.FETCH_ACTIVE_BOARD_ERROR:
      return state.merge({
        isLoadingActiveBoard: false,
      });

    //
    // EXPAND_COLLAPSE_WEEK_COLUMN
    //
    case types.EXPAND_COLLAPSE_WEEK_COLUMN: {
      const wasExpanded = state.viewConfig.expandedColumns.indexOf(action.payload.weekColumnId) > -1;
      const newExpandedColumnsArray = wasExpanded
        ? state.viewConfig.expandedColumns.filter((columnNumber) => columnNumber !== action.payload.weekColumnId)
        : state.viewConfig.expandedColumns.concat(action.payload.weekColumnId);

      return state.merge({
        viewConfig: {
          expandedColumns: newExpandedColumnsArray,
        },
      });
    }
    //
    // FETCH ROOM MEMBERS
    //
    case types.FETCH_ROOM_MEMBERS_REQUEST:
      return state.merge({ isLoadingRoomMembers: true, roomMembersRequested: { ...action.payload.params } });
    case types.FETCH_ROOM_MEMBERS_SUCCESS:
      return state.merge({ isLoadingRoomMembers: false, roomMembers: action.payload.roomMembers });
    case types.FETCH_ROOM_MEMBERS_ERROR:
      return state.merge({ isLoadingRoomMembers: false });
    //
    // SCROLL POS
    //
    case types.SET_TILE_SCROLL_POS:
      return state.merge({
        viewConfig: {
          ...state.viewConfig,
          tileScrollPos: action.payload.scrollPos,
        },
      });
    case types.SET_WEEK_SCROLL_POS:
      return state.merge({
        viewConfig: {
          ...state.viewConfig,
          weekScrollPos: action.payload.scrollPos,
        },
      });
    default:
      return state;
  }
};

// Actions
export const actions = {
  //
  // FETCH BOARDS
  //
  fetchBoardsRequest: (params: any) => ({
    type: types.FETCH_BOARDS_REQUEST,
    payload: { params },
  }),
  fetchBoardsSuccess: ({ boards }: { boards: Board[] }) => ({
    type: types.FETCH_BOARDS_SUCCESS,
    payload: { boards },
  }),
  fetchBoardsError: ({ error }: { error: any }) => ({
    type: types.FETCH_BOARDS_ERROR,
    payload: { error },
  }),
  //
  // FETCH CURRENT BOARD
  //
  fetchActiveBoardRequest: (params: any) => ({
    type: types.FETCH_ACTIVE_BOARD_REQUEST,
    payload: { params },
  }),
  fetchActiveBoardSuccess: ({ activeBoard }: { activeBoard: Board }) => ({
    type: types.FETCH_ACTIVE_BOARD_SUCCESS,
    payload: { activeBoard },
  }),
  fetchActiveBoardError: ({ error }: { error: any }) => ({
    type: types.FETCH_ACTIVE_BOARD_ERROR,
    payload: { error },
  }),
  //
  // EXPAND / COLLAPSE WEEK VIEW COLUMNS
  //
  expandCollapseWeekViewColumn: ({ weekColumnId }: { weekColumnId: string }) => ({
    type: types.EXPAND_COLLAPSE_WEEK_COLUMN,
    payload: { weekColumnId },
  }),
  //
  // FETCH ROOM MEMBERS
  //
  fetchRoomMembersRequest: (params: any) => ({
    type: types.FETCH_ROOM_MEMBERS_REQUEST,
    payload: { params },
  }),
  fetchRoomMembersSuccess: ({ roomMembers }: { roomMembers: User[] }) => ({
    type: types.FETCH_ROOM_MEMBERS_SUCCESS,
    payload: { roomMembers },
  }),
  fetchRoomMembersError: ({ error }: { error: any }) => ({
    type: types.FETCH_ROOM_MEMBERS_ERROR,
    payload: { error },
  }),
  //
  // SCROLL POS
  //
  setTileScrollPos: ({ scrollPos }: { scrollPos: any }) => ({
    type: types.SET_TILE_SCROLL_POS,
    payload: { scrollPos },
  }),
  setWeekScrollPos: ({ scrollPos }: { scrollPos: any }) => ({
    type: types.SET_WEEK_SCROLL_POS,
    payload: { scrollPos },
  }),
};

// Selectors
const boardsSelector = () => (state: any) => state.boards;

export const selectIsLoadingBoards = () =>
  createSelector(boardsSelector(), (boardsState: any) => boardsState.isLoadingBoards);

export const selectBoards = () => createSelector(boardsSelector(), (boardsState: any) => boardsState.boards);

export const selectIsLoadingActiveBoard = () =>
  createSelector(boardsSelector(), (boardsState: any) => boardsState.isLoadingActiveBoard);

export const selectIsRequestedActiveBoard = () =>
  createSelector(boardsSelector(), (boardsState: any) => boardsState.isRequestedActiveBoard);

export const selectBoard = () => createSelector(boardsSelector(), (boardsState: any) => boardsState.activeBoard);

export const selectActiveBoard = () =>
  createSelector(selectBoard(), selectRoomMembers(), selectRoomMembersRequested(), (board, roomMembers, requested) => {
    if (
      !board ||
      requested.communityId !== board.workflow_config.community_id ||
      requested.roomId !== board.workflow_config.room_id
    ) {
      return board;
    }

    const { fields, allowed_from_authorities, allowed_to_authorities } = board.field_config;

    const field_config = {
      ...board.field_config,
      allowed_from_authorities: handleDeletedAuthorities(allowed_from_authorities, roomMembers),
      allowed_to_authorities: handleDeletedAuthorities(allowed_to_authorities, roomMembers),
      fields: handleDeletedAuthoritiesInFieldConfigs(fields, roomMembers),
    };

    const delivery_config = board.delivery_config
      ? {
          ...board.delivery_config,
          fields: handleDeletedAuthoritiesInFieldConfigs(board.delivery_config.fields, roomMembers),
          allowed_from_authorities: handleDeletedAuthorities(
            board.delivery_config.allowed_from_authorities,
            roomMembers,
          ),
        }
      : board.delivery_config;

    const milestones_config =
      board.milestones_config?.length > 0
        ? board.milestones_config.map((config: MilestoneConfig) => ({
            ...config,
            fields: handleDeletedAuthoritiesInFieldConfigs(config.fields, roomMembers),
          }))
        : board.milestones_config;

    return Immutable({ ...board, field_config, delivery_config, milestones_config });
  });

export const selectBoardConfig = (type = '') =>
  createSelector(
    selectActiveBoard(),
    selectViewConfig(),
    selectExpandedColumns(),
    (board: Board, viewConfig: any, expandedColumns: any) =>
      getBoardConfig(board.field_config, type, viewConfig, expandedColumns),
  );

export const selectExpandedColumns = () =>
  createSelector(boardsSelector(), (boardsState: any) => boardsState.viewConfig.expandedColumns);

export const selectRoomMembers = () => createSelector(boardsSelector(), (boardsState: any) => boardsState.roomMembers);
export const selectRoomMembersRequested = () =>
  createSelector(boardsSelector(), (boardsState: any) => boardsState.roomMembersRequested);
export const selectIsLoadingRoomMembers = () =>
  createSelector(boardsSelector(), (boardsState: any) => boardsState.isLoadingRoomMembers);

export const selectIsRequested = () => createSelector(boardsSelector(), (boardsState: any) => boardsState.isRequested);

export const selectTileScrollPos = () =>
  createSelector(boardsSelector(), (boardsState: any) => boardsState.viewConfig.tileScrollPos);

export const selectWeekScrollPos = () =>
  createSelector(boardsSelector(), (boardsState: any) => boardsState.viewConfig.weekScrollPos);
